import React, { useState, useEffect } from 'react';
import { X, Bike, Edit2, Save, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AddressInput } from '../AddressInput';

interface AddressDetails {
  address: string;
  lat: number;
  lng: number;
}

export function AssignBikeModal({ caseId, onClose, onBikeAssigned }) {
  const [loading, setLoading] = useState(false);
  const [availableBikes, setAvailableBikes] = useState([]);
  const [selectedBike, setSelectedBike] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [addressDetails, setAddressDetails] = useState<AddressDetails | null>(null);

  useEffect(() => {
    fetchAvailableBikes();
    fetchCaseData();
  }, []);

  const fetchAvailableBikes = async () => {
    try {
      const { data, error } = await supabase
        .from('bikes')
        .select('*')
        .eq('status', 'available')
        .order('make', { ascending: true });

      if (error) throw error;
      setAvailableBikes(data || []);
    } catch (error) {
      console.error('Error fetching available bikes:', error);
    }
  };

  const fetchCaseData = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('case_number, delivery_address')
        .eq('id', caseId)
        .single();

      if (error) throw error;
      setCaseData(data);
      
      // Pre-populate delivery address if it exists
      if (data.delivery_address) {
        setDeliveryAddress(data.delivery_address);
      }
    } catch (error) {
      console.error('Error fetching case data:', error);
    }
  };

  const handleAddressSelect = (details: AddressDetails) => {
    setDeliveryAddress(details.address);
    setAddressDetails(details);
  };

  const handleBikeSelect = (bike) => {
    setSelectedBike(bike);
    setShowConfirmation(true);
  };

  const handleAssignBike = async () => {
    if (!selectedBike || !deliveryAddress) return;

    setLoading(true);
    try {
      // Update case with bike assignment and delivery details
      const { error: caseError } = await supabase
        .from('cases')
        .update({
          bike_id: selectedBike.id,
          rental_start_date: new Date().toISOString(),
          daily_rate: selectedBike.rate_a,
          status: 'bike_delivered',
          delivery_address: deliveryAddress,
          delivery_coordinates: addressDetails ? {
            lat: addressDetails.lat,
            lng: addressDetails.lng
          } : null
        })
        .eq('id', caseId);

      if (caseError) throw caseError;

      // Update bike status to delivered
      const { error: bikeError } = await supabase
        .from('bikes')
        .update({ status: 'delivered' })
        .eq('id', selectedBike.id);

      if (bikeError) throw bikeError;

      onBikeAssigned();
    } catch (error) {
      console.error('Error assigning bike:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <Bike className="h-6 w-6 text-[#6563ff]" />
            <h2 className="text-2xl font-bold text-gray-800">Assign Bike</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        {!showConfirmation ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Available Bikes
              </label>
              <div className="space-y-2">
                {availableBikes.map(bike => (
                  <div
                    key={bike.id}
                    onClick={() => handleBikeSelect(bike)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-[#6563ff] cursor-pointer transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{bike.make} {bike.model}</p>
                        <p className="text-sm text-gray-600">Rego: {bike.rego_number}</p>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-600">Rate A: ${bike.rate_a}</p>
                          <p className="text-sm text-gray-600">Rate B: ${bike.rate_b}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {availableBikes.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                No bikes available for assignment
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-[#6563ff]/5 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Confirm Assignment</h3>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Case Number:</span> {caseData?.case_number}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Bike:</span> {selectedBike.make} {selectedBike.model}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Registration:</span> {selectedBike.rego_number}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Daily Rate:</span> ${selectedBike.rate_a}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery Address
              </label>
              <AddressInput
                value={deliveryAddress}
                onChange={setDeliveryAddress}
                onSelect={handleAddressSelect}
                placeholder="Enter delivery address..."
              />
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Back
              </button>
              <button
                onClick={handleAssignBike}
                disabled={loading || !deliveryAddress}
                className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors disabled:opacity-50"
              >
                {loading ? 'Assigning...' : 'Confirm Assignment'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}